import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { useStoreSlug } from "@/hooks/use-store-slug";
import { useStores } from "@/hooks/use-stores";
import {
	getProductsAdapter,
	type Product,
} from "../adapters/get-products.adapter";

/**
 * Shared query definition so the route loader (prefetch on hover) and the hook
 * read/write the exact same cache entry. Inherits the global QueryClient
 * defaults (staleTime 5min, no refetch on focus).
 */
export function productsQueryOptions(
	storeSlug: string | undefined,
	storeId: string | undefined,
) {
	return {
		queryKey: ["products", storeSlug, storeId] as const,
		queryFn: () => getProductsAdapter(storeId!),
	};
}

export const useProducts = () => {
	const storeSlug = useStoreSlug();
	const { stores } = useStores();

	const currentStore = useMemo(() => {
		if (!storeSlug) return null;
		return stores.find((store) => store.slug === storeSlug) || null;
	}, [storeSlug, stores]);

	const {
		data: productsData = [],
		isLoading,
		error,
	} = useQuery<Product[], Error>({
		...productsQueryOptions(storeSlug, currentStore?.id),
		enabled: !!currentStore?.id,
	});

	const products = productsData.map((product: Product, index: number) => {
		const id = product.id || `temp-${index}`;

		const image = product.image || product.images?.[0] || undefined;

		const title = product.title || product.name || `Produto ${index + 1}`;

		const slug = product.slug || title.toLowerCase().replace(/\s+/g, "-");

		const status = product.status || "draft";

		return {
			id,
			image,
			title,
			slug,
			status,
			sortOrder: product.sortOrder != null ? (product.sortOrder as number) : undefined,
		};
	});

	return {
		products: products,
		isLoading,
		error: error
			? error instanceof Error
				? error
				: new Error("Failed to load products")
			: null,
	};
};
